import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import { Form, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button, Field, FormAlerts } from '@cordata/buffet';
import * as Yup from 'yup';
import { startCase, camelCase } from 'lodash';

import {
  createRecord,
  updateRecord,
} from '~/utils/situation-tables/reference-data-api';
import useSlice from '~/utils/slice/hook';

const formikEnhancer = withFormik({
  displayName: 'SitReferenceDataFormikEnhancer',
  validateOnChange: false,
  validateOnBlur: false,

  mapPropsToValues: (props) => {
    const { data, meta } = props;
    const nameCol = camelCase(meta.name_column);
    const labelCol = camelCase(meta.label_column);

    if (data && data.id) {
      return {
        id: data.id,
        [nameCol]: data[nameCol] || data[meta.name_column] || '',
        [labelCol]: data[labelCol] || data[meta.label_column] || '',
        org_uid: data.org_uid || '',
        division_uid: data.division_uid || '',
      };
    }

    return {
      [nameCol]: '',
      [labelCol]: '',
      org_uid: '',
      division_uid: '',
    };
  },

  validationSchema: (props) => {
    const { meta } = props;
    const nameCol = camelCase(meta.name_column);
    const labelCol = camelCase(meta.label_column);

    return Yup.object().shape({
      [nameCol]: Yup.string()
        .required('Name is required')
        .max(50, 'Name must be 50 characters or less')
        .matches(
          /^[\w\s\-_().,]+$/,
          'Name can only contain letters, numbers, spaces, and punctuation',
        )
        .trim(),
      [labelCol]: Yup.string()
        .required('Label is required')
        .max(50, 'Label must be 50 characters or less')
        .trim(),
    });
  },

  handleSubmit: (values, { ...bag }) => {
    const { props: { type, meta, onSuccess } } = bag;
    const nameCol = meta.name_column;
    const labelCol = meta.label_column;
    const nameColCamel = camelCase(nameCol);
    const labelColCamel = camelCase(labelCol);

    const payload = {
      [nameCol]: values[nameColCamel],
      [labelCol]: values[labelColCamel],
    };

    // Handle org/division context
    payload.org_uid = values.org_uid?.value || values.org_uid || null;
    payload.division_uid = values.division_uid?.value || values.division_uid || null;

    bag.setSubmitting(true);

    const savePromise = values.id
      ? updateRecord(type, values.id, payload)
      : createRecord(type, payload);

    savePromise
      .then(() => {
        if (onSuccess) onSuccess();
      })
      .catch((e) => {
        console.error(e);
        bag.setErrors({ server: e });
      })
      .finally(() => bag.setSubmitting(false));
  },
});

const SitReferenceDataForm = ({
  errors,
  handleSubmit,
  isSubmitting,
  submitCount,
  values,
  setFieldValue,
  onCancel,
  isOpen,
  meta,
  type,
}) => {
  const { fetch } = useSlice();
  const [orgOptions, setOrgOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);

  const nameCol = camelCase(meta.name_column);
  const labelCol = camelCase(meta.label_column);
  const displayName = startCase(type.replace(/_/g, ' ')).replace(/s$/, '');

  useEffect(() => {
    fetch('organization').then(({ data: { data } }) => {
      setOrgOptions(data.map((org) => ({ value: org.id, label: org.label })));
    });
  }, []);

  useEffect(() => {
    if (values.org_uid) {
      const orgUid = typeof values.org_uid === 'object' ? values.org_uid.value : values.org_uid;
      const divisionUid = typeof values.division_uid === 'object' ? values.division_uid.value : values.division_uid;

      fetch('division', { 'org-uid': orgUid }).then(({ data: { data } }) => {
        const divOptions = data.map((div) => ({ value: div.id, label: div.label }));
        setDivisionOptions(divOptions);
        // if the current set division_uid is not found in the options, clear the field
        if (!divOptions.find((div) => div.value === divisionUid)) {
          setFieldValue('division_uid', '');
        }
      });
    } else {
      setDivisionOptions([]);
      if (values.division_uid) {
        setFieldValue('division_uid', '');
      }
    }
  }, [values.org_uid]);

  return (
    <Modal isOpen={isOpen} backdrop="static" size="lg">
      <Form onSubmit={handleSubmit} noValidate>
        <ModalHeader>{values.id ? 'Edit' : 'Add'} {displayName}</ModalHeader>

        <ModalBody>
          {submitCount >= 1 && errors && <FormAlerts errors={errors} />}

          <Field
            name={nameCol}
            label="Name"
            required
          />

          <Field
            name={labelCol}
            label="Label"
            required
          />

          <Field
            name="org_uid"
            label="Organization (optional)"
            type="select"
            options={orgOptions}
            isClearable
          />

          <Field
            name="division_uid"
            label="Division (optional)"
            type="select"
            options={divisionOptions}
            disabled={!values.org_uid}
            isClearable
          />
        </ModalBody>

        <ModalFooter className="d-flex">
          <div className="ml-auto">
            <Button
              type="button"
              outline
              disabled={isSubmitting}
              onClick={onCancel}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Working ...' : 'Save'}
            </Button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

SitReferenceDataForm.propTypes = {
  errors: PropTypes.shape(),
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  submitCount: PropTypes.number.isRequired,
  values: PropTypes.shape({
    id: PropTypes.string,
    org_uid: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ]),
    division_uid: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ]),
  }).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  meta: PropTypes.shape({
    name_column: PropTypes.string.isRequired,
    label_column: PropTypes.string.isRequired,
  }).isRequired,
  type: PropTypes.string.isRequired,
};

SitReferenceDataForm.defaultProps = {
  errors: {},
};

export default formikEnhancer(SitReferenceDataForm);
